import i18next from 'i18next';

import normalizeStringValue from '@commons/utils/normalizeStringValue';

const getSheetConfiguration = () => ({
  ID: 'products',
  NAME: i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SHEET_NAME'),
});

const SHEET = {
  ID: 'products',
  NAME: 'Produits',
};

const COLUMN_IDS = {
  NAME: 'name',
  BRAND: 'brand',
  CATEGORY: 'category',
  SUB_CATEGORY: 'subCategory',
  SKU: 'sku',
  SHELF_LIFE: 'shelfLife',
  PRICE_WITH_TAXES: 'priceWithTaxes',
  VAT_RATE: 'vatRate',
  COMPOSITION_TYPE: 'compositionTypes',
  ASSOCIATED_RECIPE: 'associatedRecipe',
  ASSOCIATED_INGREDIENT: 'associatedIngredient',
  PORTION_SIZE_IN_GRAMS: 'portionSizeInGrams',
};

const getNormalizedColumnIds = ({ isBatchUpdate = false } = {}) => ({
  ...(isBatchUpdate && { ID: { key: 'id', normalizedKey: 'id' } }),
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.NAME')),
  },
  BRAND: {
    key: 'brand',
    normalizedKey: normalizeStringValue(i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.BRANDS')),
  },
  CATEGORY: {
    key: 'category',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.CATEGORY'),
    ),
  },
  SUB_CATEGORY: {
    key: 'subCategory',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SUB_CATEGORY'),
    ),
  },
  SKU: {
    key: 'sku',
    normalizedKey: normalizeStringValue(i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SKU')),
  },
  SHELF_LIFE: {
    key: 'shelfLife',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SHELF_LIFE'),
    ),
  },
  PRICE_WITH_TAXES: {
    key: 'priceWithTaxes',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.PRICE_WITH_TAXES'),
    ),
  },
  VAT_RATE: {
    key: 'vatRate',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.VAT_RATE'),
    ),
  },
  COMPOSITION_TYPE: {
    key: 'compositionTypes',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.COMPOSITION_TYPE'),
    ),
  },
  ASSOCIATED_RECIPE: {
    key: 'associatedRecipe',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.ASSOCIATED_RECIPE'),
    ),
  },
  ASSOCIATED_INGREDIENT: {
    key: 'associatedIngredient',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.ASSOCIATED_INGREDIENT'),
    ),
  },
  PORTION_SIZE_IN_GRAMS: {
    key: 'portionSizeInGrams',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.PORTION_SIZE_IN_GRAMS'),
    ),
  },
});

// Category
const getHiddenSheetCategoryConfig = () => ({
  SHEET: {
    ID: 'categories',
    NAME: i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_CATEGORY_NAME'),
  },
  COLUMN_ID: {
    NAME: 'name',
  },
});

const getHiddenSheetCategoryNormalizedColumnIds = () => ({
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_CATEGORY_COLUMN_NAME'),
    ),
  },
});

// Subcategory
const getHiddenSheetSubCategoryConfig = () => ({
  SHEET: {
    ID: 'subCategories',
    NAME: i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_SUB_CATEGORY_NAME'),
  },
});

const getHiddenSheetSubCategoryNormalizedColumnIds = () => ({
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_SUB_CATEGORY_COLUMN_NAME'),
    ),
  },
});

// Brands
const getHiddenSheetBrandConfig = () => ({
  SHEET: {
    ID: 'brands',
    NAME: i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_BRAND_NAME'),
  },
});

const getHiddenSheetBrandNormalizedColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'brandid',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_BRAND_COLUMN_NAME'),
    ),
  },
});

// Associated ingredient
const getHiddenSheetAssociatedIngredientConfig = () => ({
  SHEET: {
    ID: 'associatedIngredient',
    NAME: i18next.t(
      '_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_ASSOCIATED_INGREDIENT_NAME',
    ),
  },
});

const getHiddenSheetAssociatedIngredientNormalizedColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'ingredientid',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t(
        '_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_ASSOCIATED_INGREDIENT_COLUMN_NAME',
      ),
    ),
  },
});

// Associated recipe
const getHiddenSheetAssociatedRecipeConfig = () => ({
  SHEET: {
    ID: 'associatedRecipe',
    NAME: i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_ASSOCIATED_RECIPE_NAME'),
  },
  COLUMN_ID: {
    ID: 'id',
    NAME: 'name',
  },
  NORMALIZE_COLUMN_ID: {
    ID: 'recipeid',
    NAME: i18next
      .t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_ASSOCIATED_RECIPE_COLUMN_NAME')
      .toLowerCase(),
  },
});

const getHiddenSheetAssociatedRecipeNormalizedColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'recipeid',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t(
        '_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.HIDDEN_SHEET_ASSOCIATED_RECIPE_COLUMN_NAME',
      ),
    ),
  },
});
// For central kitchen products
const HIDDEN_SHEET_CATEGORY = {
  ID: 'categories',
  NAME: 'Catégories',
};

const HIDDEN_CATEGORY_COLUMN_ID = {
  NAME: 'name',
};

const HIDDEN_CATEGORY_NORMALIZE_COLUMN_ID = {
  NAME: 'nom',
};

const HIDDEN_SHEET_SUB_CATEGORY = {
  ID: 'subCategories',
  NAME: 'SousCatégories',
};

const HIDDEN_SUB_CATEGORY_COLUMN_ID = {
  NAME: 'name',
};

const HIDDEN_SUB_CATEGORY_NORMALIZE_COLUMN_ID = {
  NAME: 'nom',
};

export default {
  SHEET,
  COLUMN_IDS,
  getSheetConfiguration,
  getNormalizedColumnIds,
  // Category
  getHiddenSheetCategoryConfig,
  getHiddenSheetCategoryNormalizedColumnIds,
  // Subcategory
  getHiddenSheetSubCategoryConfig,
  getHiddenSheetSubCategoryNormalizedColumnIds,
  // Brand
  getHiddenSheetBrandConfig,
  getHiddenSheetBrandNormalizedColumnIds,
  // Associated ingredient
  getHiddenSheetAssociatedIngredientConfig,
  getHiddenSheetAssociatedIngredientNormalizedColumnIds,
  // Associated recipe
  getHiddenSheetAssociatedRecipeConfig,
  getHiddenSheetAssociatedRecipeNormalizedColumnIds,
  // For central kitchen products
  HIDDEN_SHEET_CATEGORY,
  HIDDEN_CATEGORY_COLUMN_ID,
  HIDDEN_CATEGORY_NORMALIZE_COLUMN_ID,
  HIDDEN_SHEET_SUB_CATEGORY,
  HIDDEN_SUB_CATEGORY_COLUMN_ID,
  HIDDEN_SUB_CATEGORY_NORMALIZE_COLUMN_ID,
};
